import { z } from "zod";
import { userDetailStore, UserDetailType } from "../../../stores/userDetailStore";
import { createFormFieldHandler } from "../../../utils/form-handler";
import { disableButton, enableButton } from "../../../lib/utility";
import { userMetaService } from "../../../services/userMetaService";
import { addToast } from "../../../toasts";
import { userDetailService } from "../../../services/userDetailService";

export function Notification() {
    let userDetailStoreUnsubscribe: () => void;
    let $vfNotification: null | HTMLInputElement = null;
    let $afNotification: null | HTMLInputElement = null;
    let $tfNotification: null | HTMLInputElement = null;
    let $weeklySummaryNotification: null | HTMLInputElement = null;
    let $formElem: null | HTMLFormElement = null;
    let $submitButton: null | HTMLButtonElement = null;

    const formSchema = z.object({
        video_feed_notification: z.boolean().or(z.string()),
        audio_feed_notification: z.boolean().or(z.string()),
        text_feed_notification: z.boolean().or(z.string()),
        summary_email_notification: z.boolean().or(z.string()),
    });

    type FormData = z.infer<typeof formSchema>;

    function init() {
        $vfNotification = document.getElementById("video-feed-notification") as HTMLInputElement;
        $afNotification = document.getElementById("audio-feed-notification") as HTMLInputElement;
        $tfNotification = document.getElementById("text-feed-notification") as HTMLInputElement;
        $weeklySummaryNotification = document.getElementById("weekly-summary-notification") as HTMLInputElement;
        $formElem = document.getElementById("notification-form") as HTMLFormElement;
        $submitButton = document.getElementById("notification-form-submit-btn") as HTMLButtonElement;
        const closeBtn = document.getElementById('close-profile-modal-btn') as HTMLButtonElement;

        closeBtn.addEventListener('click', () => {
            $formElem?.reset();
            populate(userDetailStore.userDetail);
        });

        const { setFieldError, validateOnBlur } = createFormFieldHandler(
            formSchema,
            $formElem
        );
        // Add event listeners to form fields to validate on blur
        validateOnBlur();

        $formElem.addEventListener("submit", async (e) => {
            e.preventDefault();

            if (!$formElem || !$submitButton) {
                console.error("No Account details to update!");
                return;
            }

            var formData = new FormData($formElem!);
            formData.append("video_feed_notification", $vfNotification!.checked ? "1" : "0");
            formData.append("audio_feed_notification", $afNotification!.checked ? "1" : "0");
            formData.append("text_feed_notification", $tfNotification!.checked ? "1" : "0");
            formData.append("summary_email_notification", $weeklySummaryNotification!.checked ? "1" : "0");
            const data = Object.fromEntries(formData.entries());

            try {
                formSchema.parse(data);
                disableButton($submitButton!);
                try {
                    const response: any = await userMetaService.update(userDetailStore.userDetail.id, formData);
                    populateUserMeta();
                    addToast({ toastBody: response.message }, "default", "success");
                    enableButton($submitButton!);
                } catch (error: any) {
                    // this catch block is for handling Server errors on the request
                    console.error("Update account details request failed: ", error);
                    addToast({ toastBody: error.message }, "default", "error");
                    error.errors && Object.keys(error.errors).forEach((field) => {
                        // Display each error message for the corresponding field
                        setFieldError(field as keyof FormData, error.errors[field][0]);
                    });
                    enableButton($submitButton!);
                }
            } catch (error) {
                // this catch block is for handling Frontend form validation
                if (error instanceof z.ZodError) {
                    error.errors.forEach((err) => {
                        setFieldError(err.path[0] as keyof FormData, err.message);
                    });
                }
                console.error("Form data is invalid:", error);
            } finally {
                enableButton($submitButton!);
            }
        });

        userDetailStoreUnsubscribe = userDetailStore.subscribe(function ({ userDetail, isLoading }) {
            if(!isLoading) populate(userDetail)
        });
        if(!userDetailStore.loading) populate(userDetailStore.userDetail)
    }
    function populate(userDetail: UserDetailType) {
        if (userDetail && userDetail.user_meta) {
            $vfNotification!.checked = userDetail.user_meta.video_feed_notification == true;
            $afNotification!.checked = userDetail.user_meta.audio_feed_notification == true;
            $tfNotification!.checked = userDetail.user_meta.text_feed_notification == true;
            $weeklySummaryNotification!.checked = userDetail.user_meta.summary_email_notification == true;
        }
    }

    async function populateUserMeta(isFetch: boolean = true) {
        if (isFetch) {
            await userDetailService.fetchDetail();
        }        
    }

    function cleanUp() {
        if (userDetailStoreUnsubscribe) {
            userDetailStoreUnsubscribe();
        }
    }

    function render() {
        return /*html*/`
        <div id="vertical-tab-myaccount-notifications" class="hidden" role="tabpanel" aria-labelledby="vertical-tab-myaccount-notifications">
        <form id="notification-form">
        <!-- Account Card -->
        <div
            class="md:min-h-[calc(100vh-90px)] p-4 md:p-5 bg-white border border-gray-200 shadow-sm rounded-xl dark:bg-neutral-800 dark:border-neutral-700">
            <!-- Notifications -->
            <div class="space-y-8">
            <!-- Title -->
            <div>
                <h2 class="text-lg font-semibold text-gray-800 dark:text-neutral-200">
                Notification Preferences
                </h2>
                <p class="text-sm text-gray-500 dark:text-neutral-500">
                Stay updated! We'll notify you via email.
                </p>
            </div>
            <!-- End Title -->

            <!-- Grid -->
            <div class="grid sm:grid-cols-1 gap-y-6 sm:gap-y-0 sm:gap-x-10 md:gap-x-16">
                <div class="space-y-6">
                <!-- Switch -->
                <label for="video-feed-notification" class="flex gap-x-4 cursor-pointer w-full">
                    <input type="checkbox" id="video-feed-notification"
                    class="relative mt-1 shrink-0 w-[44px] h-6 p-px bg-gray-200 border-transparent text-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:ring-blue-600 disabled:opacity-50 disabled:pointer-events-none checked:bg-none checked:text-blue-600 checked:border-blue-600 focus:checked:border-blue-600 dark:bg-neutral-700 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-neutral-600

                    before:inline-block before:size-5 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:rounded-full before:shadow before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 dark:before:bg-neutral-400 dark:checked:before:bg-white"
                    checked="">
                    <span class="flex flex-col">
                    <span class="block text-sm font-medium text-gray-800 dark:text-neutral-300">
                        Video Review
                    </span>
                    <span class="block text-sm text-gray-500 dark:text-neutral-500 max-w-[350px]">
                        You will get email when you receive Video reviews from all the workspaces you have access to.
                    </span>
                    </span>
                </label>
                <!-- End Switch -->

                <!-- Switch -->
                <label for="audio-feed-notification" class="cursor-pointer w-full flex gap-x-4">
                    <input type="checkbox" id="audio-feed-notification"
                    class="relative mt-1 shrink-0 w-[44px] h-6 p-px bg-gray-200 border-transparent text-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:ring-blue-600 disabled:opacity-50 disabled:pointer-events-none checked:bg-none checked:text-blue-600 checked:border-blue-600 focus:checked:border-blue-600 dark:bg-neutral-700 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-neutral-600

                    before:inline-block before:size-5 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:rounded-full before:shadow before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 dark:before:bg-neutral-400 dark:checked:before:bg-white"
                    checked="">
                    <span class="flex flex-col">
                    <span class="block text-sm font-medium text-gray-800 dark:text-neutral-300">
                        Audio Review
                    </span>
                    <span class="block text-sm text-gray-500 dark:text-neutral-500 max-w-[350px]">
                        You will get email when you receive Audio reviews from all the workspaces you have access to.
                    </span>
                    </span>
                </label>
                <!-- End Switch -->

                <!-- Switch -->
                <label for="text-feed-notification" class="flex gap-x-4 cursor-pointer w-full">
                    <input type="checkbox" id="text-feed-notification"
                    class="relative mt-1 shrink-0 w-[44px] h-6 p-px bg-gray-200 border-transparent text-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:ring-blue-600 disabled:opacity-50 disabled:pointer-events-none checked:bg-none checked:text-blue-600 checked:border-blue-600 focus:checked:border-blue-600 dark:bg-neutral-700 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-neutral-600

                    before:inline-block before:size-5 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:rounded-full before:shadow before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 dark:before:bg-neutral-400 dark:checked:before:bg-white"
                    checked="">
                    <span class="flex flex-col">
                    <span class="block text-sm font-medium text-gray-800 dark:text-neutral-300">
                        Text Review
                    </span>
                    <span class="block text-sm text-gray-500 dark:text-neutral-500 max-w-[350px]">
                        You will get email when you receive Text reviews from all the workspaces you have access to.
                    </span>
                    </span>
                </label>
                <!-- End Switch -->

                <!-- Switch -->
                <label for="weekly-summary-notification" class="flex gap-x-4 cursor-pointer w-full hidden">
                    <input type="checkbox" id="weekly-summary-notification"
                    class="relative mt-1 shrink-0 w-[44px] h-6 p-px bg-gray-200 border-transparent text-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:ring-blue-600 disabled:opacity-50 disabled:pointer-events-none checked:bg-none checked:text-blue-600 checked:border-blue-600 focus:checked:border-blue-600 dark:bg-neutral-700 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-neutral-600

                    before:inline-block before:size-5 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:rounded-full before:shadow before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 dark:before:bg-neutral-400 dark:checked:before:bg-white"
                    checked="">
                    <span class="flex flex-col">
                    <span class="block text-sm font-medium text-gray-800 dark:text-neutral-300">
                        Weekly summary
                    </span>
                    <span class="block text-sm text-gray-500 dark:text-neutral-500">
                        Weekly summary of the feeds from all workspaces.
                    </span>
                    </span>
                </label>
                <!-- End Switch -->

                <div class="flex gap-x-3">
                    <button type="submit"
                    id="notification-form-submit-btn"
                    class="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span class="btn-label flex gap-x-1 items-center">
                    Save Changes
                    </span>
                    </button>
                </div>
                </div>
                <!-- End Col -->
            </div>
            <!-- End Grid -->

            </div>
            <!-- End Notifications -->
        </div>
        <!-- End Account Card -->
        </form>
        </div>
        `;
    }
    return { render, init, cleanUp };
}